'use client'

import { ChevronDown, ChevronUp, SlidersHorizontal } from '@/components/icons'
import { PRODUCT_TYPE_FIELDS } from '@/lib/generated/product-type-fields'
import { isValidProductTypeSlug } from '@shared/generated/productConditions'
import { FilterChipSelect } from './filter-chip'
import { useListingBrowseContext } from '../context/listing-browse-context'
import { cn } from '@/lib/utils'
import { Button } from "@/components/ui/button";

export function AdvancedFiltersToggle() {
 const {
  productType,
  advancedFiltersExpanded,
  toggleAdvancedFilters,
  specFilters,
 } = useListingBrowseContext()

 const fields =
  productType && isValidProductTypeSlug(productType)
   ? PRODUCT_TYPE_FIELDS[productType]
   : null
 const availableFilters = fields?.filter(
  (filter) => filter.type === 'select' && filter.name !== 'condition'
 )

 if (!availableFilters || availableFilters.length === 0) {
  return null
 }

 return (
  <Button
   type="button"
   variant="outline"
   size="compact"
   onClick={toggleAdvancedFilters}
   aria-expanded={advancedFiltersExpanded}
   className={cn(
    'rounded-sm',
    advancedFiltersExpanded || specFilters.length > 0
     ? 'bg-muted text-foreground'
     : 'text-muted-foreground'
   )}
  >
   <SlidersHorizontal className="w-3.5 h-3.5" />
   <span>More</span>
   {specFilters.length > 0 && (
    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold bg-action-fill text-action-fill-foreground rounded-sm">
     {specFilters.length}
    </span>
   )}
   {advancedFiltersExpanded ? (
    <ChevronUp className="w-3.5 h-3.5" />
   ) : (
    <ChevronDown className="w-3.5 h-3.5" />
   )}
  </Button>
 )
}

function filterableSpecFields(productType: string) {
 const fields = isValidProductTypeSlug(productType) ? PRODUCT_TYPE_FIELDS[productType] : undefined
 if (!fields) return undefined
 return fields.filter(
  (field) => field.type === 'select' && field.name !== 'condition'
 )
}

export function AdvancedFiltersPanel() {
 const {
  productType,
  advancedFiltersExpanded,
  specFilters,
  addSpecFilter,
  removeSpecFilter,
 } = useListingBrowseContext()

 if (!advancedFiltersExpanded || !productType) {
  return null
 }

 const filterableFields = filterableSpecFields(productType)
 if (!filterableFields || filterableFields.length === 0) {
  return null
 }

 return (
  <div className="flex items-center gap-2 px-4 py-2 bg-accent border-t border-border">
   <span className="text-xs font-medium text-muted-foreground mr-2">
    Specs
   </span>
   {filterableFields.map((field) => {
    if (field.type !== 'select' || !field.options) return null

    const currentValue = specFilters.find((filter) => filter.field === field.name)?.value
    const selectedValues = currentValue ? [currentValue] : []

    return (
     <FilterChipSelect
      key={field.name}
      label={field.label}
      options={field.options}
      value={selectedValues}
      onChange={(values) => {
       if (values.length > 0) {
        addSpecFilter({ field: field.name, value: values[0] })
       } else {
        removeSpecFilter(field.name)
       }
      }}
      multiple={false}
     />
    )
   })}
  </div>
 )
}
